import { useState } from 'react';
import styled, { keyframes } from 'styled-components';

import { usePasswordStrength } from '../../common/hooks/usePasswordStrength';

import { Text } from '../../cdl/Text/Text.tsx';
import { translate } from '../../common/helpers/translate.helper';
import { IconInfoCircle } from '../../common/icons/cdl/InfoCircle';
import { Box } from '../../common/ui/Box.tsx';
import { theme } from '../../common/ui/theme';

import { ProgressBar } from './ProgressBar';

const WarningBox = styled(Box)`
    text-align: left;
    user-select: none;
`;
const SuggestionList = styled(Box)``;
const SuggestionItem = styled(Box)``;

const colors = [
    theme.colors.negative.emphasis,
    theme.colors.negative.emphasis,
    theme.colors.attention.emphasis,
    theme.colors.positive.emphasis,
    theme.colors.positive.emphasis,
];

const Bounce = keyframes`
    0% {
        transform: scale(1);
    }
    50% {
        transform: scale(1.05);
    }
    100% {
        transform: scale(1.0);
    }
`;

const ProgressWrap = styled(Box)`
    display: flex;
    color: ${theme.colors.accent.foreground};
    cursor: pointer;

    &.is-full {
        animation-name: ${Bounce};
        animation-duration: 150ms;
        animation-delay: 175ms;
        animation-timing-function: ease-in-out;
    }

    &.is-open,
    &:hover {
        color: ${theme.colors.accent.emphasis};
    }
`;

export const StrengthMeter = ({ text = '' }) => {
    const [showFeedback, setShowFeedback] = useState(false);
    const { score, feedback } = usePasswordStrength(text);

    return (
        <div>
            <ProgressWrap
                alignItems="center"
                onClick={() => setShowFeedback(!showFeedback)}
                className={[showFeedback ? 'is-open' : null, score === 4 ? 'is-full' : null].join(' ')}
            >
                <ProgressBar progress={text === '' ? 0 : (score + 1) / 5} color={colors[score]} marginRight={8} />
                <IconInfoCircle />
            </ProgressWrap>

            {showFeedback ? (
                <WarningBox
                    backgroundColor="background.subtle"
                    borderRadius={4}
                    border="default"
                    marginTop={8}
                    padding={24}
                >
                    <Text as="p">{translate('passwordStrength.feedbackText')}</Text>
                    {score === 4 ? (
                        <Text color="positive.foreground" marginTop={8} weight="bold" as="p">
                            {translate('passwordStrength.strongPassword')}
                        </Text>
                    ) : null}

                    {feedback.warning ? (
                        <Text color="negative.foreground" weight="bold" marginTop={8}>
                            Warning: {feedback.warning}
                        </Text>
                    ) : null}

                    {feedback.suggestions?.length > 0 ? (
                        <SuggestionList as="ul" marginTop={8} paddingLeft={24} marginBottom={0}>
                            {feedback.suggestions.map((suggestion, key) => (
                                <SuggestionItem as="li" key={key}>
                                    <Text display="inline">{suggestion}</Text>
                                </SuggestionItem>
                            ))}
                        </SuggestionList>
                    ) : null}
                </WarningBox>
            ) : null}
        </div>
    );
};
